// API service for fetching vehicle data from the valt-vehicles API endpoint
import { isSingleSchemaEnabled, regionIdFromCode } from '@/services/regionScope'
import { UnifiedVehicleApiService } from '@/services/unifiedVehicleApiService'
import { unifiedVehicleToApiVehicle } from '@/utils/singleSchemaAdapters'
import { coerceVaSelectValue } from '@/utils/assetTypeUtils'
import { getAuthHeaders, parseValtVehicleResponseRegion, valtFetch, VALT_API_BASE } from '@/utils/api'
import type { UnifiedVehicle } from '@/types/vehicle'
import { matchesAssetListSearch } from '@/utils/assetListSearch'

export interface ApiVehicle {
  id: number
  deviceId: string | null
  name: string | null
  year: string | null
  model: string
  number_plate: string | null
  colour: string | null
  status_on_gometro: string | null
  status_on_amp_control: string | null
  status_on_valt_app: string | null
  status_on_uber: string | null
  initial_linked_driver: string | null
  licensePlate: string | null
  vin: string
  car_make: string
  status: string
  /** Licensing / compliance rollup from API (optional until backend ships field). */
  compliance_status?: string | null
  /** `vehicles.compliance` — plain string, JSON string, structured object, or null. */
  compliance?: string | Record<string, unknown> | null
  /** Nested document approvals from listing/detail payload. */
  compliance_documents?: Record<string, { file_path?: string | null; url?: string | null; approved?: boolean }> | null
  /** Nested compliance payload from API (.NET-style `Compliance` property). */
  Compliance?:
    | {
        compliance_status?: string | null
        ComplianceStatus?: string | null
        status?: string | null
        Status?: string | null
        documents?: unknown
        Documents?: unknown
      }
    | string
    | null
  primary_status?: string | null
  sub_status?: string | null
  status_label?: string | null
  status_changed_at?: string | null
  status_changed_by?: string | null
  created_at: string
  updated_at: string
  /** Set client-side from GET envelope `region` / `connection` when present. */
  api_region?: string | null
  /** Asset classification (bike | car | cargo). */
  asset_type?: string | null
  /** API alias for asset classification. */
  vehicle_type?: string | null
  type?: string | null
}

export interface VehicleApiResponse {
  success: boolean
  data: ApiVehicle[]
  pagination: {
    current_page: number
    per_page: number
    total: number
    last_page: number
    from?: number
    to?: number
  }
}

/** Response from GET /valt-vehicles/search (no pagination) */
export interface VehicleSearchApiResponse {
  success?: boolean
  data: ApiVehicle[]
  total: number
  region?: string
  connection?: string
}

export interface CreateVehicleData {
  vin: string
  car_make: string
  model: string
  year?: string | null
  number_plate?: string | null
  colour?: string | null
  status_on_gometro?: string | null
  status_on_amp_control?: string | null
  status_on_valt_app?: string | null
  status_on_uber?: string | null
  initial_linked_driver?: string | null
  licensePlate?: string | null
  deviceId?: string | null
  name?: string | null
  status?: string
  compliance_status?: string | null
  /** Asset classification sent to POST /api/vehicles (text slug, e.g. bike). */
  vehicle_type?: string | null
  /** Some APIs accept a nested `Compliance` object on PATCH alongside snake_case fields. */
  Compliance?: { compliance_status?: string | null; ComplianceStatus?: string | null }
  po_number?: string | null
  po_batch?: string | null
  po_date?: string | null
  po_order_date?: string | null
  po_landed_date?: string | null
}

export interface UpdateVehicleData extends Partial<CreateVehicleData> {
  id: number
}

export interface VehicleFilters {
  vin?: string
  car_make?: string
  model?: string
  colour?: string
  status?: string
  per_page?: number
  page?: number
  /** Explicit region override (e.g. 'johannesburg') */
  region?: string
  /** Server-side asset filter (bike, car, cargo). */
  vehicle_type?: string
}

export class VehicleApiService {
  private static readonly API_BASE_URL = VALT_API_BASE

  // Fetch all vehicles from the API with pagination and filters
  static async getVehicles(filters?: VehicleFilters): Promise<VehicleApiResponse> {
    if (isSingleSchemaEnabled()) {
      const result = await UnifiedVehicleApiService.list({
        ...filters,
        primary_status: filters?.status,
      })
      return {
        success: result.success,
        data: result.data.map(unifiedVehicleToApiVehicle),
        pagination: result.pagination,
      }
    }

    try {
      const params = new URLSearchParams()

      // Add pagination params (default per_page 500)
      params.append('page', (filters?.page || 1).toString())
      params.append('per_page', (filters?.per_page ?? 500).toString())

      // Add filter params
      if (filters?.vin) params.append('vin', filters.vin)
      if (filters?.car_make) params.append('car_make', filters.car_make)
      if (filters?.model) params.append('model', filters.model)
      if (filters?.colour) params.append('colour', filters.colour)
      if (filters?.status) params.append('status', filters.status)
      if (filters?.vehicle_type) params.append('vehicle_type', filters.vehicle_type)

      // Add region parameter if available
      const region = filters?.region || localStorage.getItem('region')
      if (region) params.append('region', region)

      const queryString = params.toString()
      const fullUrl = `${this.API_BASE_URL}/valt-vehicles${queryString ? `?${queryString}` : ''}`

      console.log('Fetching vehicles from URL:', fullUrl)

      const response = await valtFetch(fullUrl, {
        method: 'GET',
        mode: 'cors',
        headers: getAuthHeaders(),
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch vehicles: ${response.status} ${response.statusText}`)
      }

      const data: VehicleApiResponse = await response.json()
      console.log('Fetched vehicles from API:', data)
      return data
    } catch (error) {
      console.error('Error fetching vehicles:', error)
      // Provide more specific error messages
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to the API. Please check your internet connection.')
      }
      throw error
    }
  }

  /**
   * Search vehicles across list fields (id, VIN, plate, make, model, year, color, status, type).
   * Single-schema: pages /api/vehicles and matches any displayed field.
   * Legacy: GET /valt-vehicles/search?search=TERM&region=...&limit=...
   */
  static async searchVehicles(searchTerm: string, options?: { limit?: number }): Promise<VehicleSearchApiResponse> {
    if (!searchTerm?.trim()) {
      return { data: [], total: 0 }
    }

    if (isSingleSchemaEnabled()) {
      const limit = Math.min(options?.limit ?? 500, 1000)
      const term = searchTerm.trim()
      const byId = new Map<number | string, UnifiedVehicle>()

      // Prefer server-side plate/VIN filters with no region scope so NULL region_id assets match.
      const [plateResult, vinResult] = await Promise.all([
        UnifiedVehicleApiService.list({ number_plate: term, per_page: limit, page: 1 }),
        UnifiedVehicleApiService.list({ vin: term, per_page: limit, page: 1 }),
      ])
      for (const v of [...plateResult.data, ...vinResult.data]) {
        byId.set(v.id, v)
      }

      if (/^\d+$/.test(term)) {
        const byPk = await UnifiedVehicleApiService.getById(term)
        if (byPk) byId.set(byPk.id, byPk)
      }

      // Fall back to paged scan only when targeted lookups miss (name / make / etc.).
      if (byId.size === 0) {
        let page = 1
        let lastPage = 1
        do {
          const result = await UnifiedVehicleApiService.list({ page, per_page: 500 })
          lastPage = result.pagination.last_page || 1
          for (const v of result.data) {
            if (matchesAssetListSearch(v as unknown as Record<string, unknown>, term)) {
              byId.set(v.id, v)
              if (byId.size >= limit) break
            }
          }
          page++
        } while (page <= lastPage && byId.size < limit)
      }

      const data = [...byId.values()].slice(0, limit).map(unifiedVehicleToApiVehicle)
      return { data, total: data.length }
    }

    try {
      const params = new URLSearchParams()
      params.append('search', searchTerm.trim())
      const region = localStorage.getItem('region')
      if (region) params.append('region', region)
      const limit = Math.min(options?.limit ?? 500, 1000)
      params.append('limit', limit.toString())

      const queryString = params.toString()
      const fullUrl = `${this.API_BASE_URL}/valt-vehicles/search?${queryString}`

      const response = await valtFetch(fullUrl, {
        method: 'GET',
        mode: 'cors',
        headers: getAuthHeaders(),
      })

      if (!response.ok) {
        throw new Error(`Failed to search vehicles: ${response.status} ${response.statusText}`)
      }

      const result = await response.json()
      const data = result.data ?? []
      const total = typeof result.total === 'number' ? result.total : data.length
      return { data, total, region: result.region, connection: result.connection }
    } catch (error) {
      console.error('Error searching vehicles:', error)
      throw error
    }
  }

  // Fetch all vehicles from the /vehicle endpoint (for uber/vehicles route)
  // This endpoint returns a direct array, not a wrapped response
  static async getUberVehicles(filters?: VehicleFilters): Promise<VehicleApiResponse> {
    if (isSingleSchemaEnabled()) {
      const result = await UnifiedVehicleApiService.list({
        page: filters?.page,
        per_page: filters?.per_page,
        vin: filters?.vin,
        model: filters?.model,
      })
      const withUber = result.data
        .filter((v) => Boolean(v.uber_vehicle_uuid ?? v.uber_vehicle?.vehicleId))
        .map(unifiedVehicleToApiVehicle)
      return {
        success: result.success,
        data: withUber,
        pagination: { ...result.pagination, total: withUber.length },
      }
    }

    try {
      const params = new URLSearchParams()

      // Add pagination params (default per_page 500)
      params.append('page', (filters?.page || 1).toString())
      params.append('per_page', (filters?.per_page ?? 500).toString())

      // Add filter params
      if (filters?.vin) params.append('vin', filters.vin)
      if (filters?.car_make) params.append('car_make', filters.car_make)
      if (filters?.model) params.append('model', filters.model)
      if (filters?.colour) params.append('colour', filters.colour)
      if (filters?.status) params.append('status', filters.status)

      // Add region parameter if available
      const region = localStorage.getItem('region')
      if (region) params.append('region', region)

      const queryString = params.toString()
      const fullUrl = `${this.API_BASE_URL}/vehicle${queryString ? `?${queryString}` : ''}`

      console.log('Fetching uber vehicles from URL:', fullUrl)

      const response = await valtFetch(fullUrl, {
        method: 'GET',
        mode: 'cors',
        headers: getAuthHeaders(),
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch vehicles: ${response.status} ${response.statusText}`)
      }

      const data: any = await response.json()
      console.log('Fetched vehicles from API:', data)

      // Handle new paginated response format with vehicles array
      if (data && typeof data === 'object' && 'vehicles' in data && Array.isArray(data.vehicles)) {
        const page = data.page || filters?.page || 1
        const perPage = data.per_page || filters?.per_page || 500
        const total = data.total || data.vehicles_count || 0
        const vehicles = data.vehicles || []

        return {
          success: true,
          data: vehicles,
          pagination: {
            current_page: page,
            per_page: perPage,
            total: total,
            last_page: Math.ceil(total / perPage),
            from: (page - 1) * perPage + 1,
            to: (page - 1) * perPage + vehicles.length,
          },
        }
      }

      // Handle direct array response (legacy format)
      if (Array.isArray(data)) {
        const page = filters?.page || 1
        const perPage = filters?.per_page ?? 500

        // If we got fewer results than per_page, we're on the last page
        // Otherwise, we estimate total by assuming there might be more pages
        const isLastPage = data.length < perPage
        const total = isLastPage ? (page - 1) * perPage + data.length : page * perPage + 1 // Estimate - will be updated if we fetch next page

        return {
          success: true,
          data: data, // Already paginated by server
          pagination: {
            current_page: page,
            per_page: perPage,
            total: total,
            last_page: isLastPage ? page : Math.ceil(total / perPage),
            from: (page - 1) * perPage + 1,
            to: (page - 1) * perPage + data.length,
          },
        }
      }

      // Handle wrapped response format (legacy)
      return data as VehicleApiResponse
    } catch (error) {
      console.error('Error fetching vehicles:', error)
      // Provide more specific error messages
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to the API. Please check your internet connection.')
      }
      throw error
    }
  }

  // Get vehicle by ID (region comes from API envelope when possible — not localStorage)
  static async getVehicleById(id: number, opts?: { region?: string | null }): Promise<ApiVehicle | null> {
    if (isSingleSchemaEnabled()) {
      const hint = opts?.region != null && String(opts.region).trim() !== '' ? String(opts.region).trim() : undefined
      const vehicle = await UnifiedVehicleApiService.getById(id, { region: hint })
      return vehicle ? unifiedVehicleToApiVehicle(vehicle) : null
    }

    try {
      const hint = opts?.region != null && String(opts.region).trim() !== '' ? String(opts.region).trim() : undefined
      const regionParam = hint ? `?region=${encodeURIComponent(hint)}` : ''
      const headers = getAuthHeaders()
      if (hint) headers['X-Region'] = hint

      const response = await valtFetch(`${this.API_BASE_URL}/valt-vehicles/${id}${regionParam}`, {
        method: 'GET',
        mode: 'cors',
        headers,
      })

      if (!response.ok) {
        if (response.status === 404) {
          return null
        }
        throw new Error(`Failed to fetch vehicle: ${response.status} ${response.statusText}`)
      }

      const raw = (await response.json()) as Record<string, unknown>
      const vehicle = (raw.data ?? raw) as ApiVehicle | null
      if (!vehicle || typeof vehicle !== 'object') {
        return null
      }
      const apiRegion = parseValtVehicleResponseRegion(raw) ?? hint ?? null
      if (apiRegion) {
        ;(vehicle as ApiVehicle).api_region = apiRegion
      }
      return vehicle
    } catch (error) {
      console.error('Error fetching vehicle by ID:', error)
      throw error
    }
  }

  // Create new vehicle
  static async createVehicle(vehicleData: CreateVehicleData, regionCode?: string): Promise<ApiVehicle> {
    if (isSingleSchemaEnabled()) {
      const region = regionCode ?? localStorage.getItem('region') ?? undefined
      const regionId = regionIdFromCode(region)
      const created = await UnifiedVehicleApiService.create(
        {
          vin: vehicleData.vin,
          car_make: vehicleData.car_make,
          model: vehicleData.model,
          number_plate: vehicleData.number_plate,
          colour: vehicleData.colour,
          year: vehicleData.year,
          device_id: vehicleData.deviceId,
          initial_linked_driver: vehicleData.initial_linked_driver,
          primary_status: coerceVaSelectValue(vehicleData.status) || undefined,
          vehicle_type: coerceVaSelectValue(vehicleData.vehicle_type) || undefined,
          ...(regionId != null ? { region_id: regionId } : {}),
        },
        region,
      )
      return unifiedVehicleToApiVehicle(created)
    }

    try {
      console.log('Creating vehicle with data:', vehicleData)

      const region = regionCode ?? localStorage.getItem('region')
      const regionParam = region ? `?region=${region}` : ''

      const response = await valtFetch(`${this.API_BASE_URL}/valt-vehicles${regionParam}`, {
        method: 'POST',
        mode: 'cors',
        headers: getAuthHeaders(),
        body: JSON.stringify(vehicleData),
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('API Error Response:', errorText)
        try {
          const errorData = JSON.parse(errorText)
          console.error('Parsed Error Data:', errorData)
          throw new Error(
            `Failed to create vehicle: ${response.status} ${response.statusText} - ${JSON.stringify(errorData)}`,
          )
        } catch (parseError) {
          throw new Error(`Failed to create vehicle: ${response.status} ${response.statusText} - ${errorText}`)
        }
      }

      const data = await response.json()
      // Handle both direct vehicle object and wrapped response
      const createdVehicle = data.data || data
      console.log('Vehicle created successfully:', createdVehicle)
      return createdVehicle
    } catch (error) {
      console.error('Error creating vehicle:', error)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to the API. Please check your internet connection.')
      }
      throw error
    }
  }

  // Update vehicle (`apiRegion` from vehicle GET envelope takes precedence over localStorage)
  static async updateVehicle(
    id: number,
    vehicleData: Partial<CreateVehicleData>,
    apiRegion?: string | null,
  ): Promise<ApiVehicle> {
    if (isSingleSchemaEnabled()) {
      const region =
        apiRegion != null && String(apiRegion).trim() !== ''
          ? String(apiRegion).trim()
          : typeof localStorage !== 'undefined'
            ? localStorage.getItem('region') || undefined
            : undefined
      const updated = await UnifiedVehicleApiService.update(
        id,
        {
          car_make: vehicleData.car_make,
          model: vehicleData.model,
          number_plate: vehicleData.number_plate,
          colour: vehicleData.colour,
          year: vehicleData.year,
          device_id: vehicleData.deviceId,
          initial_linked_driver: vehicleData.initial_linked_driver,
          primary_status: vehicleData.status,
          po_number: vehicleData.po_number,
          po_batch: vehicleData.po_batch,
          po_date: vehicleData.po_date,
          po_order_date: vehicleData.po_order_date,
          po_landed_date: vehicleData.po_landed_date,
        },
        region,
      )
      return unifiedVehicleToApiVehicle(updated)
    }

    try {
      console.log(`Updating vehicle ${id} with data:`, vehicleData)

      const region =
        apiRegion != null && String(apiRegion).trim() !== ''
          ? String(apiRegion).trim()
          : typeof localStorage !== 'undefined'
            ? localStorage.getItem('region') || undefined
            : undefined
      const regionParam = region ? `?region=${encodeURIComponent(region)}` : ''

      const headers = getAuthHeaders()
      if (region) headers['X-Region'] = region
      const response = await valtFetch(`${this.API_BASE_URL}/valt-vehicles/${id}${regionParam}`, {
        method: 'PATCH',
        mode: 'cors',
        headers,
        body: JSON.stringify(vehicleData),
      })

      if (!response.ok) {
        const errorText = await response.text()
        let detail = errorText
        try {
          const errJson = JSON.parse(errorText) as { message?: string }
          if (typeof errJson.message === 'string' && errJson.message.trim()) {
            detail = errJson.message.trim()
          }
        } catch {
          /* keep raw body */
        }
        if (response.status === 404) {
          throw new Error(detail || 'Vehicle not found (404). Check id and region.')
        }
        throw new Error(detail || `Failed to update vehicle: ${response.status} ${response.statusText}`)
      }

      const data = (await response.json()) as Record<string, unknown>
      const updatedVehicle = (data.data ?? data) as ApiVehicle
      const envRegion =
        parseValtVehicleResponseRegion(data) ??
        (apiRegion != null && String(apiRegion).trim() !== '' ? String(apiRegion).trim() : null)
      if (envRegion && updatedVehicle && typeof updatedVehicle === 'object') {
        updatedVehicle.api_region = envRegion
      }
      console.log('Vehicle updated successfully:', updatedVehicle)
      return updatedVehicle
    } catch (error) {
      console.error('Error updating vehicle:', error)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to the API. Please check your internet connection.')
      }
      throw error
    }
  }

  /**
   * Delete Valt vehicle by numeric ID or license plate (case-insensitive).
   * API: DELETE /api/valt-vehicles/{id} with ?region=... or X-Region header.
   */
  static async deleteVehicle(id: number | string): Promise<boolean> {
    if (isSingleSchemaEnabled()) {
      const region = localStorage.getItem('region') || undefined
      return UnifiedVehicleApiService.delete(id, region)
    }

    try {
      const region = localStorage.getItem('region')
      const regionParam = region ? `?region=${region}` : ''
      const pathId = typeof id === 'string' ? encodeURIComponent(id.trim()) : id

      const headers = getAuthHeaders()
      if (region) headers['X-Region'] = region
      const response = await valtFetch(`${this.API_BASE_URL}/valt-vehicles/${pathId}${regionParam}`, {
        method: 'DELETE',
        mode: 'cors',
        headers,
      })

      if (response.status === 404) {
        const body = await response.json().catch(() => ({}))
        throw new Error(body?.message || 'Vehicle not found')
      }

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Failed to delete vehicle: ${response.status} ${response.statusText} - ${errorText}`)
      }

      return true
    } catch (error) {
      console.error('Error deleting vehicle:', error)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to the API. Please check your internet connection.')
      }
      throw error
    }
  }
}
